import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

function PolicyHeading({ index, children, className = "" }) {
  return (
    <h2 className={`text-[20px] font-semibold text-[#1d1d1f] mb-4 flex items-center gap-3 ${className}`}>
      <span className="text-[12px] font-bold text-[#295195] tabular-nums" aria-hidden="true">{index}</span>
      {children}
    </h2>
  );
}

export default function PolicyPage() {
  const section = "mb-10";
  const ul = "list-disc list-inside text-[14px] text-[#6e6e73] leading-relaxed space-y-1.5";
  const box = "bg-[#f5f5f7] rounded-2xl p-5 text-[14px] text-[#6e6e73] leading-relaxed";

  return (
    <div className="bg-white min-h-screen">
      <SEO title="교환/반품 정책" path="/policy" />
      <div className="max-w-[780px] mx-auto px-4 pt-10 pb-20">
        <h1 className="text-[28px] md:text-[36px] font-semibold text-[#1d1d1f] tracking-tight mb-2">교환 · 반품 · 환불 정책</h1>
        <p className="text-[15px] text-[#86868b] mb-10">일광오피스는 고객님의 안전한 거래를 위해 아래 정책을 운영합니다.</p>

        {/* 교환/반품 기간 */}
        <div className={section}>
          <PolicyHeading index="01">교환 · 반품 기간</PolicyHeading>
          <div className={box}>
            <p className="font-semibold text-[#1d1d1f] mb-2">상품 수령 후 7일 이내 교환/반품 신청이 가능합니다.</p>
            <ul className={ul}>
              <li>계약 서면을 받은 날보다 상품 공급이 늦으면 상품을 받은 날부터 기간을 계산합니다. 내용 확인을 위한 포장 개봉만으로 반품을 제한하지 않습니다.</li>
              <li>표시·광고 또는 계약과 다른 상품은 수령 후 3개월 이내이면서 그 사실을 안 날 또는 알 수 있었던 날부터 30일 이내에 신청할 수 있습니다.</li>
              <li>렌탈은 개별 계약 조건과 관계 법령에 따릅니다. <Link to="/terms" className="underline">이용약관 확인</Link></li>
            </ul>
          </div>
        </div>

        {/* 교환/반품 가능 사유 */}
        <div className={section}>
          <PolicyHeading index="02">교환 · 반품이 가능한 경우</PolicyHeading>
          <ul className={ul}>
            <li>배송 중 파손 또는 외관 손상이 발생한 경우</li>
            <li>주문한 상품과 다른 상품이 배송된 경우 (오배송)</li>
            <li>상품의 기능 불량 또는 결함이 있는 경우</li>
            <li>표시·광고 내용과 다르게 이행된 경우</li>
          </ul>
        </div>

        {/* 교환/반품 불가 사유 */}
        <div className={section}>
          <PolicyHeading index="03">교환 · 반품이 불가한 경우</PolicyHeading>
          <ul className={ul}>
            <li>소비자의 책임으로 상품이 훼손된 경우(내용 확인을 위한 포장 훼손 제외)</li>
            <li>소비자의 사용·소비로 상품 가치가 현저히 감소한 경우</li>
            <li>주문 제작품은 회복하기 어려운 중대한 피해가 예상되어 사전 별도 고지와 서면 동의 등 법정 요건을 갖춘 경우</li>
            <li>법령에서 정한 청약철회 기간이 지난 경우. 하자·계약 불일치는 위의 별도 기간을 적용합니다.</li>
          </ul>
        </div>

        {/* 교환/반품 비용 */}
        <div className={section}>
          <PolicyHeading index="04">교환 · 반품 비용</PolicyHeading>
          <div className="overflow-hidden rounded-xl border border-[#d2d2d7]">
            <table className="w-full text-[14px]">
              <thead className="bg-[#f5f5f7]">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-[#1d1d1f]">사유</th>
                  <th className="px-4 py-3 text-left font-semibold text-[#1d1d1f]">배송비 부담</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="px-4 py-3 text-[#6e6e73]">상품 불량 / 오배송</td>
                  <td className="px-4 py-3 text-[#34c759] font-semibold">무료 (판매자 부담)</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-[#6e6e73]">단순 변심</td>
                  <td className="px-4 py-3 text-[#6e6e73]">왕복 배송비 고객 부담</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* 환불 안내 */}
        <div className={section}>
          <PolicyHeading index="05">환불 안내</PolicyHeading>
          <ul className={ul}>
            <li>반품 상품 회수 확인 후 <strong>3영업일 이내</strong> 환불 처리됩니다.</li>
            <li>카드 결제: 카드사 승인 취소 (3~7 영업일 소요)</li>
            <li>계좌이체: 계좌로 직접 환불 (1~3 영업일 소요)</li>
            <li>후불 결제: 청구 취소 처리</li>
          </ul>
        </div>

        {/* 교환/반품 절차 */}
        <div className={section}>
          <PolicyHeading index="06">교환 · 반품 절차</PolicyHeading>
          <div className="space-y-3">
            {[
              { step: "1", title: "접수", desc: "고객센터 전화 또는 마이페이지에서 교환/반품 신청" },
              { step: "2", title: "확인", desc: "담당자가 접수 내용 확인 후 안내 연락" },
              { step: "3", title: "회수", desc: "택배사를 통한 상품 회수 (불량/오배송 시 착불)" },
              { step: "4", title: "처리", desc: "상품 확인 후 교환 발송 또는 환불 처리" },
            ].map((s) => (
              <div key={s.step} className="flex items-start gap-3">
                <div className="w-8 h-8 shrink-0 rounded-full bg-[#0071e3] text-white flex items-center justify-center text-[13px] font-bold">{s.step}</div>
                <div>
                  <p className="text-[14px] font-semibold text-[#1d1d1f]">{s.title}</p>
                  <p className="text-[13px] text-[#86868b]">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 문의처 */}
        <div className="bg-[#0071e3]/5 rounded-2xl p-6 border border-[#0071e3]/10">
          <PolicyHeading index="07" className="text-[17px] mb-3">교환 · 반품 문의</PolicyHeading>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[14px]">
            <div>
              <p className="text-[#86868b]">전화</p>
              <p className="font-semibold text-[#1d1d1f]">1600-7542</p>
            </div>
            <div>
              <p className="text-[#86868b]">이메일</p>
              <p className="font-semibold text-[#1d1d1f]">master@sktec.kr</p>
            </div>
            <div>
              <p className="text-[#86868b]">운영시간</p>
              <p className="font-semibold text-[#1d1d1f]">평일 09:00 ~ 18:00 (공휴일 제외)</p>
            </div>
            <div>
              <p className="text-[#86868b]">마이페이지</p>
              <Link to="/my" className="font-semibold text-[#0071e3] hover:underline">주문 내역 확인 →</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
